import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, CalendarDays } from "lucide-react";
import { BLOG_POSTS } from "@/lib/blog-posts";
import Seo from "@/components/site/Seo";
import Breadcrumbs from "@/components/site/Breadcrumbs";

const BLOG_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Blog",
  name: "Elite Home Care by Hortense Blog",
  url: "https://yourelitehomecare.com/blog",
  description:
    "Honest, SWFL-specific guidance for families navigating in-home senior care.",
};

export default function Blog() {
  return (
    <div data-testid="page-blog">
      <Seo
        path="/blog"
        title="SWFL Senior Care Blog — Honest Guidance for Families"
        description="Practical, local guidance for families navigating in-home senior care in Naples, Fort Myers, Cape Coral & Bonita Springs. Written by Hortense's team."
        jsonLd={BLOG_JSONLD}
      />

      <section className="bg-navy-900 text-white">
        <div className="container-x pt-20 pb-16 md:pt-24 md:pb-20">
          <Breadcrumbs
            dark
            crumbs={[{ name: "Home", path: "/" }, { name: "Resources" }]}
          />
          <h1 className="mt-6 font-serif text-5xl md:text-6xl leading-[1.05] max-w-3xl">
            Honest guidance for SWFL families.
          </h1>
          <p className="mt-5 max-w-2xl text-white/80 text-lg leading-relaxed">
            Practical, specific, locally-rooted writing on the questions
            families actually ask us. No fluff. No sales.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container-x grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {BLOG_POSTS.map((p) => (
            <article
              key={p.slug}
              data-testid={`blog-card-${p.slug}`}
              className="card-soft overflow-hidden flex flex-col group"
            >
              <Link to={`/blog/${p.slug}`} className="block">
                <div className="aspect-[16/10] overflow-hidden bg-navy-100">
                  <img
                    src={p.heroImage}
                    alt={p.heroAlt}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </Link>
              <div className="p-7 flex flex-col flex-1">
                <div className="flex items-center gap-4 text-xs text-navy-500">
                  <span className="inline-flex items-center gap-1">
                    <CalendarDays className="h-3.5 w-3.5" />
                    {new Date(p.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {p.readMinutes} min read
                  </span>
                </div>
                <Link to={`/blog/${p.slug}`} className="mt-4">
                  <h2 className="font-serif text-2xl text-navy-900 leading-snug group-hover:text-gold-600 transition-colors">
                    {p.title}
                  </h2>
                </Link>
                <p className="mt-3 text-navy-600 leading-relaxed flex-1">
                  {p.excerpt}
                </p>
                <Link
                  to={`/blog/${p.slug}`}
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-navy-900 group-hover:text-gold-500 transition-colors"
                >
                  Read article <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
